import type { Format, Subtitle, DownloadedSubtitle } from "~/types/subtitle";
import { ITEMS_PER_PAGE } from "./index";
import { getLanguageDisplayName } from "./languages";
import { zipSync } from "fflate";

export const sanitizeFilename = (input: string): string => {
  // Only allow alphanumeric characters, spaces, and some symbols
  const invalidChars = /[<>:"/\\|?*\x00-\x1F]/g;

  return input
    .replace(invalidChars, "") // Remove invalid characters
    .replace(/\.\./g, ".") // Replace multiple dots with a single dot
    .replace(/^\.|\.$/g, "") // Remove dots at the beginning and end
    .trim(); // Remove trailing spaces
};

export const saveSubtitle = async (
  content: string,
  title: string,
  format: Format
) => {
  const fileName = `${title ? sanitizeFilename(title) : "subtitle"}.${format}`;

  // Detect iOS and in-app browsers
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isInApp =
    /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent) ||
    /(iPhone|iPod|iPad).*CriOS/i.test(navigator.userAgent) ||
    /(iPhone|iPod|iPad).*FxiOS/i.test(navigator.userAgent) ||
    /GSA/i.test(navigator.userAgent); // Google Search App

  // Try Web Share API first for iOS and in-app browsers
  if ((isIOS || isInApp) && navigator.share && navigator.canShare) {
    try {
      const blob = new Blob([content], { type: "text/plain" });
      const file = new File([blob], fileName, { type: "text/plain" });

      // Check if can share files
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: fileName,
          text: "Save subtitle file",
        });
        return;
      }
    } catch (error) {
      // User cancelled share dialog → no fallback download
      const isAbort =
        (error instanceof DOMException && error.name === "AbortError") ||
        (error && typeof error === "object" && (error as Error).name === "AbortError");
      if (isAbort) return;
      console.log(
        "Web Share API failed, falling back to standard download:",
        error
      );
      // Fall through to standard download
    }
  }

  // Standard download for other browsers or if Web Share API fails
  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.style.display = "none";

  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    if (document.body.contains(a)) {
      document.body.removeChild(a);
    }
    URL.revokeObjectURL(url);
  }, 100);
};

/**
 * href for a user-supplied media URL. Pasted links often have no scheme
 * ("youtube.com/watch?v=..."), so those get https://; any other scheme
 * (javascript:, data:, ...) returns null so no link is rendered.
 */
export const safeExternalHref = (url: string | null | undefined): string | null => {
  const value = `${url || ""}`.trim();
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  if (value.startsWith("//")) return `https:${value}`;
  // A scheme, but not "host:port" (a digit after the colon).
  if (/^[a-z][a-z0-9+.-]*:(?!\d)/i.test(value)) return null;
  return `https://${value}`;
};

export const decodeHtml = (html: string) => {
  let txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
};

export const getSubtitleData = async (subtitle: Subtitle) => {
  const { $downsub } = useNuxtApp();

  Object.assign(subtitle, $downsub(subtitle.url));
  if (subtitle.state === 99) {
    if (subtitle.source === "Youtube" && subtitle.id) {
      return await getYoutubeData(subtitle);
    } else {
      return await getApiData(subtitle);
    }
  }
  return subtitle;
};
const getAppApiUrl = (): string => {
  const { appApiUrl, appMemberApiUrl } = useRuntimeConfig().public;
  const user = useSupabaseUser();
  return (user.value ? appMemberApiUrl : appApiUrl) as string;
};

export const getPlaylistData = async (
  subtitle: Subtitle,
  page: number | string = 1,
  perPage: number = 0,
  sortOrder: string = "asc"
) => {
  const { $downsub, $encode } = useNuxtApp();
  let playlist: any = {
    items: [],
    pagination: {
      next: false,
      previous: false,
    },
  };
  let { playlistId, url } = $downsub(subtitle.url);

  // 0: Youtube, 4: Viki
  const supportedSource: any[] = [
    "youtube",
    "viki",
    "viu",
    "vimeo",
    "wetv",
    "iq",
  ];

  playlistId = subtitle.playlistId || playlistId;
  const sourceName = subtitle.source?.toString().toLowerCase();
  if (playlistId && supportedSource.includes(sourceName)) {
    let apiUrl = "https://api-playlist.downsub.com";
    // let apiUrl = "http://localhost:8787";
    let urlAPI: any = new URL(`${apiUrl}/${sourceName}/${$encode(playlistId)}`);
    urlAPI.searchParams.set("page", page.toString());
    urlAPI.searchParams.set("sort", sortOrder);
    perPage > 0 && urlAPI.searchParams.set("perPage", perPage.toString());
    switch (sourceName) {
      case "youtube": // Youtube
      case "viki": // Viki
      case "iq": // IQ
        break;
      case "vimeo":
        const playlistTypeRegex =
          /^(?:http(?:s)?:\/\/)?(?:www\.)?(?:m\.)?vimeo\.com\/(showcase|albums|groups|channels)\/([a-zA-Z0-9-]+)(?:\/.*)?$/;
        const vimeoMatches = subtitle.url.match(playlistTypeRegex);
        if (vimeoMatches && vimeoMatches.length > 2) {
          urlAPI.searchParams.set("type", vimeoMatches[1]);
        }
        break;
      case "viu":
        urlAPI = new URL(`${apiUrl}/viu/${playlistId}`);
        const playlistRegex =
          /^(?:http(?:s)?:\/\/)?(?:www\.)?(?:m\.)?(?:viu\.com\/ott\/)([a-zA-Z]+)\/([a-z-]+)\/vod\/([0-9]+)/;
        const matches = subtitle.url.match(playlistRegex);
        if (matches && matches.length > 3) {
          urlAPI.searchParams.set("country", matches[1]);
          urlAPI.searchParams.set("language", matches[2]);
        }
        urlAPI.searchParams.set("page", page.toString());
        urlAPI.searchParams.set("sort", sortOrder);
        perPage > 0 && urlAPI.searchParams.set("perPage", perPage.toString());
        break;
      case "wetv":
        const playlistWetvRegex =
          /^(?:http(?:s)?:\/\/)?(?:www\.)?(?:m\.)?(?:(?:wetv|iflix)\.(?:vip|com)\/)([a-z-]+)?\/play\/([a-z0-9]+)/;
        const wetvMatches = subtitle.url.match(playlistWetvRegex);
        if (wetvMatches && wetvMatches[1]) {
          urlAPI.searchParams.set("language", wetvMatches[1]);
        }
        break;
      default:
        urlAPI = new URL(getAppApiUrl());
        urlAPI.searchParams.set("page", page.toString());
        urlAPI.searchParams.set("sort", sortOrder);
        urlAPI.searchParams.set("type", "playlist");
        urlAPI.searchParams.set("url", url);
        urlAPI.searchParams.set("id", playlistId);
    }
    const config = {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };

    await $fetch(urlAPI, {
      method: "GET",
      headers: config.headers,
    })
      .then((response: any) => {
        if (typeof response !== "undefined") {
          const data =
            typeof response === "string" ? JSON.parse(response) : response;
          if (data.items?.length > 0) {
            playlist = {
              items: data.items,
              pagination: data.pagination,
              // Kept outside `pagination`, which callers rebuild when paging.
              total: Number(data.pagination?.total) || 0,
            };
          }
        }
      })
      .catch((error: any) => {
        console.error(error);
      })
      .finally(() => {
        if (
          playlist.items.length > ITEMS_PER_PAGE &&
          playlist.items.length > perPage &&
          !playlist.pagination.next
        ) {
          playlist.pagination.next = true;
        }
      });
  }
  return playlist;
};

export const getYoutubeData = async (subtitle: Subtitle) => {
  const apiYTB = useRuntimeConfig().public.apiYtbUrl;
  const url = `${apiYTB}${subtitle.id}`;
  
  await $fetch(url)
    .then((responseData: any) => {
      if (responseData != "" && responseData.state !== 3) {
        Object.assign(subtitle, responseData);
      }
    })
    .catch((error: any) => {})
    .finally(async () => {
      if (subtitle.state == 99) {
        await getApiData(subtitle);
      } else {
        processSubtitleData(subtitle);
      }
    });
};

export const getApiData = async (subtitle: Subtitle) => {
  const { $encode } = useNuxtApp();
  const urlAPI = getAppApiUrl();
  const config = {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  };

  await $fetch(urlAPI, {
    method: "POST",
    headers: config.headers,
    body: {
      url: subtitle.url,
      data: $encode(subtitle.urlEncrypt, subtitle.url),
    },
  })
    .then((response: any) => {
      if (typeof response == "undefined") {
        Object.assign(subtitle, { state: 3, errorCode: "" });
      }
      if (typeof response !== "undefined") {
        Object.assign(subtitle, JSON.parse(response));
      }
    })
    .catch(() => {
      subtitle.state = 3;
      subtitle.errorCode = "";
    })
    .finally(() => {
      processSubtitleData(subtitle);
    });
};

export const processSubtitleData = (subtitle: any) => {
  const { $i18nGlobal } = useNuxtApp();

  switch (subtitle.state) {
    case 1:
    case 2:
      subtitle.title = subtitle.title || $i18nGlobal("common.unknown");
      break;
    default:
      subtitle.title = subtitle.title || $i18nGlobal("common.error");
  }

  // Some sources (e.g. TubiTV) return tracks with an empty name, which would
  // render as blank options and crash name-based sorting/matching downstream.
  if (subtitle.subtitles?.length > 0) {
    subtitle.subtitles = subtitle.subtitles.map((sub: any) =>
      sub && typeof sub === "object" && !sub.name
        ? {
            ...sub,
            name: sub.language
              ? getLanguageDisplayName(sub.language)
              : $i18nGlobal("common.unknown"),
          }
        : sub
    );
  }

  if (subtitle.subtitlesAutoTrans?.length > 0) {
    const usedNames = new Set(
      subtitle.subtitles?.map((sub: any) => sub.name) || []
    );

    subtitle.translatedSubtitles = subtitle.subtitlesAutoTrans.map(
      (trans: any) => {
        let uniqueName = trans.name;

        if (usedNames.has(uniqueName)) {
          uniqueName = `${trans.name} (${$i18nGlobal(
            "common.autoTranslated"
          )})`;
        }

        usedNames.add(uniqueName);
        return { ...trans, name: uniqueName };
      }
    );

    delete subtitle.subtitlesAutoTrans;
  }

  if (subtitle.playlist.length > 0) {
    const playlist = subtitle.playlist;

    subtitle.playlist = {
      items: playlist,
      pagination: {
        next: playlist.length > ITEMS_PER_PAGE,
        previous: false,
      },
    };
  }
};

// downsub-get errorCode values that have their own message (FETCH_FAILED /
// BOT_BLOCKED are transient and fall back to the generic "oops")
const ERROR_CODES_WITH_MESSAGE = [
  "VIDEO_PRIVATE",
  "VIDEO_UNAVAILABLE",
  "AGE_RESTRICTED",
  "GEO_RESTRICTED",
  "MEMBERS_ONLY",
  "LIVE_NOT_AVAILABLE",
  "VIDEO_UNPLAYABLE",
];

// Verdicts a retry can't change (private, removed, region/age locked...)
const TERMINAL_ERROR_CODES = [
  "VIDEO_PRIVATE",
  "VIDEO_UNAVAILABLE",
  "AGE_RESTRICTED",
  "GEO_RESTRICTED",
  "MEMBERS_ONLY",
  "LIVE_NOT_AVAILABLE",
];

// i18n key for a downsub-get errorCode, or null when it has no specific message
export const getErrorCodeKey = (errorCode?: string): string | null =>
  errorCode && ERROR_CODES_WITH_MESSAGE.includes(errorCode)
    ? `errors.codes.${errorCode}`
    : null;

export const getErrorCodeMessage = (errorCode?: string): string | null => {
  const key = getErrorCodeKey(errorCode);
  if (!key) {
    return null;
  }
  const { $i18nGlobal } = useNuxtApp();
  return $i18nGlobal(key);
};

export const getErrorMessage = (state = 0, errorCode?: string) => {
  const { $i18nGlobal } = useNuxtApp();

  if (state === 3) {
    const codeMessage = getErrorCodeMessage(errorCode);
    if (codeMessage) {
      return codeMessage;
    }
  }

  switch (state) {
    case 0:
      return $i18nGlobal("errors.invalid");
    case 1:
      return $i18nGlobal("errors.notfound");
    case 2:
      return $i18nGlobal("errors.notfound");
    case 3:
    case 4:
      return $i18nGlobal("errors.oops");
    default:
      return $i18nGlobal("errors.unknownError");
  }
};

export const stringifySubtitle = (cues: VTTCue[], format: Format): string => {
  const formatTime = (seconds: number, isSRT = false): string => {
    const date = new Date(seconds * 1000);
    const hours = date.getUTCHours().toString().padStart(2, "0");
    const minutes = date.getUTCMinutes().toString().padStart(2, "0");
    const secs = date.getUTCSeconds().toString().padStart(2, "0");
    const ms = date.getUTCMilliseconds().toString().padStart(3, "0");

    return `${hours}:${minutes}:${secs}${isSRT ? "," : "."}${ms}`;
  };

  if (format === "srt") {
    return cues
      .map(
        (cue, index) =>
          `${index + 1}\n${formatTime(cue.startTime, true)} --> ${formatTime(
            cue.endTime,
            true
          )}\n${cue.text}`
      )
      .join("\n\n");
  }

  if (format === "txt") {
    return cues.map((cue) => cue.text).join("\n");
  }

  // Format VTT
  return `WEBVTT\n\n${cues
    .map(
      (cue) =>
        `${formatTime(cue.startTime)} --> ${formatTime(cue.endTime)}\n${
          cue.text
        }`
    )
    .join("\n\n")}`;
};

export const convertSubtitle = (
  srtContent: string | null,
  outputFormat: "vtt" | "txt" | "srt"
): string => {
  // Check if input is empty
  if (!srtContent?.trim()) {
    return "";
  }

  // Return SRT if output format is SRT
  if (outputFormat === "srt") {
    return srtContent;
  }

  // Split subtitles into array
  const subtitles = srtContent
    .trim()
    .split(/\n\n+/)
    .filter((block) => block.trim());

  if (outputFormat === "txt") {
    // Convert to plain text
    return subtitles
      .map((subtitle) => {
        const lines = subtitle.split("\n");
        // Skip index and timestamp, only get content
        return lines.slice(2).join(" ");
      })
      .join("\n\n");
  } else {
    // Convert to VTT
    const vttParts = ["WEBVTT\n"];

    subtitles.forEach((subtitle) => {
      const lines = subtitle.split("\n");
      if (lines.length < 3) return;

      // Convert timestamp from SRT (00:00:00,000) to VTT (00:00:00.000)
      const timestamp = lines[1].replace(/,/g, ".");

      // Add subtitle to result array
      vttParts.push(timestamp);
      vttParts.push(lines.slice(2).join("\n"));
      vttParts.push("");
    });

    return vttParts.join("\n");
  }
};

export const getSubtitleContent = async (
  file: DownloadedSubtitle,
  format: Format
) => {
  const config = useRuntimeConfig();
  const { user } = useUser();
  const options = user.value?.download_options ?? {};
  const isRemoveTags = options.is_remove_tags ?? true;
  const isFixOverlapping = options.is_fix_overlapping ?? true;
  // Cleaner options default to false, so send them only when the user enabled them.
  const isRemoveSdh = options.is_remove_sdh ?? false;
  const isRemoveMusic = options.is_remove_music ?? false;
  const isRemoveDuplicates = options.is_remove_duplicates ?? false;
  const isRemoveAds = options.is_remove_ads ?? false;
  const isNormalizeUnicode = options.is_normalize_unicode ?? false;

  const subtitle = [...file.subtitles, ...file.translatedSubtitles].find(
    (sub) => sub.name === file.language
  );
  if (!subtitle) {
    return false;
  }

  // Legacy options: sent only when turned off (server default is on).
  let queryParams = !isRemoveTags ? "?isRemoveTags=false" : "";
  queryParams += !isFixOverlapping ? "&isFixOverlapping=false" : "";
  // Cleaner options: sent only when turned on (server default is off).
  queryParams += isRemoveSdh ? "&isRemoveSdh=true" : "";
  queryParams += isRemoveMusic ? "&isRemoveMusic=true" : "";
  queryParams += isRemoveDuplicates ? "&isRemoveDuplicates=true" : "";
  queryParams += isRemoveAds ? "&isRemoveAds=true" : "";
  queryParams += isNormalizeUnicode ? "&isNormalizeUnicode=true" : "";

  try {
    const content = await $fetch<string>(
      `${config.public.downloadUrl}/${format}/${subtitle?.url}${queryParams}`,
      { responseType: "text" }
    );
    return typeof content === "string" ? content : "";
  } catch {
    return false;
  }
};

export const downloadAllSubtitles = async (
  downloadedSubtitles: DownloadedSubtitle[],
  selectedFormat: Format
): Promise<{
  success: number;
  failed: { title: string; reason?: any }[];
  total: number;
}> => {
  const files: Record<string, Uint8Array> = {};
  const fileNames = new Set();

  const itemsToProcess = downloadedSubtitles.filter((file) => file.gotInfo);
  const failed: { title: string; reason?: any }[] = [];

  // Process all files in parallel
  const downloadPromises = itemsToProcess.map(async (file) => {
    try {
      const content = await getSubtitleContent(file, selectedFormat);
      if (!content) {
        failed.push({ title: file.title });
        return null;
      }

      // create unique file name
      let fileName = sanitizeFilename(file.title);
      if (!fileName) fileName = "subtitle"; // fallback if empty after sanitize
      let uniqueFileName = fileName;
      let counter = 1;
      while (fileNames.has(uniqueFileName)) {
        uniqueFileName = `${fileName} (${counter++})`;
      }
      fileNames.add(uniqueFileName);

      return {
        name: `${uniqueFileName}.${selectedFormat}`,
        content: new TextEncoder().encode(content),
      };
    } catch (error) {
      failed.push({ title: file.title, reason: error });
      return null; // Skip this file and continue with others
    }
  });

  // Wait for all promises to resolve
  const results = await Promise.all(downloadPromises);
  // Filter out null results and add to files object
  let success = 0;
  results.forEach((result) => {
    if (result) {
      files[result.name] = result.content;
      success += 1;
    }
  });

  const total = itemsToProcess.length;
  // Do not create zip if there are no valid files
  if (success === 0) {
    return { success, failed, total };
  }

  const zipContent = zipSync(files);
  const zipFileName = `subtitles_${new Date().toISOString()}.zip`;
  const blob = new Blob([new Uint8Array(zipContent)], {
    type: "application/zip",
  });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = zipFileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  return { success, failed, total };
};

export const getSubtitleInfo = async (file: DownloadedSubtitle) => {
  file.isGettingInfo = true;
  file.gotInfo = false;
  try {
    await getSubtitleData(file);

    file.isGettingInfo = false;
    file.gotInfo =
      file.state === 2 && file.subtitles?.length > 0 ? true : false;
  } catch (error: any) {
    file.state = 3;
    file.isGettingInfo = false;
    console.log(error);
  }
};

const getSubtitleInfosPooled = async (
  downloadedSubtitles: DownloadedSubtitle[],
  batchSize: number
) => {
  // Create queue with max `batchSize` requests at a time
  const queue = [...downloadedSubtitles];

  // Create a pool to track running promises
  const pool = new Set<Promise<void>>();

  while (queue.length > 0 || pool.size > 0) {
    // Add new request to pool if there's space and items in queue
    while (pool.size < batchSize && queue.length > 0) {
      const file = queue.shift()!;
      const promise = getSubtitleInfo(file).finally(() => {
        pool.delete(promise);
      });
      pool.add(promise);
    }

    // Wait until at least one request completes
    if (pool.size > 0) {
      await Promise.race(Array.from(pool));
    }
  }
};

export const getSubtitleInfos = async (
  downloadedSubtitles: DownloadedSubtitle[]
) => {
  await getSubtitleInfosPooled(downloadedSubtitles, 5);

  // Bulk fetches can hit transient errors (rate limits) that a single-video
  // fetch would not: retry those once, slower. State 1 (no captions) and
  // terminal errorCodes (private, geo/age-restricted...) are final.
  const failed = downloadedSubtitles.filter(
    (file) =>
      file.state === 3 && !TERMINAL_ERROR_CODES.includes(file.errorCode ?? "")
  );
  if (failed.length > 0) {
    await getSubtitleInfosPooled(failed, 2);
  }
};

export const formatDateTime = (timestamp: number | string, locale: string) => {
  if (!timestamp) return "";

  let date: Date;

  if (typeof timestamp === "string") {
    // If it's a string, try to parse it as a date string
    date = new Date(timestamp);
  } else {
    // If it's a number, check if it's Unix timestamp (seconds) or JavaScript timestamp (milliseconds)
    // Unix timestamps are typically 10 digits, JS timestamps are 13 digits
    if (timestamp.toString().length <= 10) {
      // Unix timestamp (seconds) - convert to milliseconds
      date = new Date(timestamp * 1000);
    } else {
      // JavaScript timestamp (milliseconds)
      date = new Date(timestamp);
    }
  }

  // Check if date is valid
  if (isNaN(date.getTime())) {
    return "";
  }

  return `${date.toLocaleDateString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })} ${date.toLocaleTimeString(locale)}`;
};

// Format a number using the active i18n locale so the thousands/decimal
// separators match the user's language (e.g. "vi" -> "2.150", "en" -> "2,150").
export const formatNumber = (value: number | null | undefined, locale: string) => {
  const n = typeof value === "number" && Number.isFinite(value) ? value : 0;
  try {
    return n.toLocaleString(locale);
  } catch {
    return n.toLocaleString();
  }
};

// Parse a duration value into seconds. Accepts "HH:MM:SS", "MM:SS", "SS",
// numeric strings ("123"), or numbers. Returns 0 when the value is empty or
// cannot be parsed.
/** Seconds in an ISO 8601 duration like "PT1H2M3S" (YouTube Data API); 0 if unparseable. */
export const parseISO8601DurationSeconds = (duration: string): number => {
  const match = `${duration || ""}`.match(/^PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?$/);
  if (!match) {
    return 0;
  }
  return Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0);
};

export const parseDurationToSeconds = (value: string | number | null | undefined): number => {
  if (value == null || value === "") {
    return 0;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  }

  const trimmed = `${value}`.trim();
  if (!trimmed) {
    return 0;
  }

  if (/^\d+(?:\.\d+)?$/.test(trimmed)) {
    const seconds = Math.floor(Number(trimmed));
    return Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  }

  const parts = trimmed.split(":");
  if (parts.length < 2 || parts.length > 3 || parts.some((part) => !/^\d+$/.test(part))) {
    return 0;
  }

  const numbers = parts.map((part) => Number(part));
  if (numbers.some((n) => !Number.isFinite(n))) {
    return 0;
  }

  if (numbers.length === 3) {
    return numbers[0] * 3600 + numbers[1] * 60 + numbers[2];
  }
  return numbers[0] * 60 + numbers[1];
};

// Convert ISO 8601 duration to readable format
export const formatISO8601Duration = (isoDuration: string): string => {
  // Convert ISO 8601 "PT1H2M3S" to "1:02:03"
  const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return "0:00";

  const hours = parseInt(match[1] || "0");
  const minutes = parseInt(match[2] || "0");
  const seconds = parseInt(match[3] || "0");

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }
};

/**
 * Extract language code from country code (e.g., pt-PT -> pt, pt_BR -> pt)
 * @param code - Language or country code
 * @returns The base language code
 */
export const getLanguageCode = (code: string): string => {
  if (code.includes("-")) {
    return code.split("-")[0];
  }
  if (code.includes("_")) {
    return code.split("_")[0];
  }
  return code;
};

/**
 * Check if subtitle code matches favorite language
 * Supports matching by language code (e.g., pt-PT matches pt)
 * @param subtitleCode - The subtitle language code
 * @param favoriteCode - The favorite language code
 * @returns true if they match, false otherwise
 */
export const matchesFavoriteLanguage = (
  subtitleCode: string,
  favoriteCode: string
): boolean => {
  // Non-YouTube subtitle tracks may have no language code - guard against it.
  if (!subtitleCode || !favoriteCode) return false;

  const subtitleLower = subtitleCode.toLowerCase();
  const favoriteLower = favoriteCode.toLowerCase();

  // Exact match
  if (subtitleLower === favoriteLower) return true;

  // Extract language codes
  const subtitleLang = getLanguageCode(subtitleLower);
  const favoriteLang = getLanguageCode(favoriteLower);

  // Match by language code (e.g., pt-PT matches pt)
  if (subtitleLang === favoriteLang) return true;

  // Match if subtitle starts with favorite (e.g., favorite: pt, subtitle: pt-PT)
  if (
    subtitleLower.startsWith(favoriteLower + "-") ||
    subtitleLower.startsWith(favoriteLower + "_")
  ) {
    return true;
  }

  // Match if favorite starts with subtitle language code (e.g., favorite: pt-PT, subtitle: pt)
  if (
    favoriteLower.startsWith(subtitleLang + "-") ||
    favoriteLower.startsWith(subtitleLang + "_")
  ) {
    return true;
  }

  return false;
};

const AUTO_GENERATED_CODE_SUFFIX = /_auto$/;

/**
 * Whether a track is a YouTube auto-generated (ASR) caption - the YouTube
 * worker suffixes their code with "_auto" (e.g. "fr_auto").
 */
export const isAutoGeneratedSubtitle = (sub: any): boolean =>
  typeof sub?.code === "string" && AUTO_GENERATED_CODE_SUFFIX.test(sub.code);

/**
 * Key used to match "the same language" across videos in bulk downloads.
 * Manual, auto-generated and regional tracks share one key
 * ("fr", "fr_auto", "fr-CA" -> "code:fr"), except script variants and Chinese
 * where the variant matters ("zh-Hant", "sr-Latn"). Tracks without a code
 * (some non-YouTube sources) fall back to their name.
 */
export const getSubtitleLanguageKey = (sub: any): string => {
  const code =
    typeof sub?.code === "string"
      ? sub.code.trim().replace(AUTO_GENERATED_CODE_SUFFIX, "")
      : "";
  if (!code) return sub?.name ? `name:${sub.name}` : "";

  const base = getLanguageCode(code).toLowerCase();
  const variant = code.slice(base.length + 1);
  const keepVariant = !!variant && (base === "zh" || /^[a-z]{4}$/i.test(variant));
  return `code:${keepVariant ? `${base}-${variant}` : base}`;
};

/**
 * Pick the track of a video to download for a language key, in order:
 * manual track -> auto-generated track -> YouTube auto-translation.
 * Within a group the plain language ("fr") wins over a regional one ("fr-CA").
 */
export const pickSubtitleForLanguage = (
  file: Pick<Subtitle, "subtitles" | "translatedSubtitles">,
  languageKey: string
): any | null => {
  if (!languageKey) return null;

  const plainCode = languageKey.startsWith("code:") ? languageKey.slice(5) : "";
  const preferPlain = (tracks: any[]) =>
    tracks.find((sub) => sub.code === plainCode) ?? tracks[0];

  const original = (file.subtitles ?? []).filter(
    (sub) => getSubtitleLanguageKey(sub) === languageKey
  );
  const manual = original.filter((sub) => !isAutoGeneratedSubtitle(sub));
  const autoGenerated = original.filter(isAutoGeneratedSubtitle);
  const translated = (file.translatedSubtitles ?? []).filter(
    (sub) => getSubtitleLanguageKey(sub) === languageKey
  );

  return (
    preferPlain(manual) ??
    preferPlain(autoGenerated) ??
    preferPlain(translated) ??
    null
  );
};
